import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GameController, Eye, EyeSlash, AppleLogo } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { SupportLink } from '../components/Support';
import { Logo } from '../components/Logo';
import { FoundersWall } from '../components/FoundersWall';
import { GoogleSignInButton, AppleSignInButton } from '../components/SocialSignIn';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      // Admins always skip onboarding entirely — go straight to /admin
      const isAdmin = result.user?.role === 'admin' || (typeof result.user === 'object' && result.user?.email === 'support@cyboard.games');
      if (isAdmin) {
        navigate('/', { replace: true });
        setLoading(false);
        return;
      }
      // Check if onboarding needed for regular users
      try {
        const ob = await axios.get(`${API}/api/onboarding/progress`, { withCredentials: true });
        if (ob.data && !ob.data.completed && !ob.data.skipped) {
          navigate('/onboarding', { replace: true });
          setLoading(false);
          return;
        }
      } catch {}
      navigate('/', { replace: true });
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="app-container flex flex-col items-center justify-center min-h-screen p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <Logo variant="full" size="xl" clickable={false} showTagline={false} />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-3 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
              data-testid="login-error"
            >
              <p>{error}</p>
              <p className="text-[11px] text-[#888] mt-1.5">
                Still stuck? Contact <SupportLink subject="CyBoard Login Issue" label="support" className="text-[#9D4CDD]" />.
              </p>
            </motion.div>
          )}

          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field"
              placeholder="your@email.com"
              required
              data-testid="login-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field pr-12"
                placeholder="••••••••"
                required
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white transition-colors"
              >
                {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="login-submit"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </button>

          <Link to="/reset-password" className="block text-center text-[#9D4CDD] text-sm mt-3 hover:underline" data-testid="forgot-password-link">
            Forgot Password?
          </Link>
        </form>

        {/* ============ SOCIAL SIGN-IN ============ */}
        <div className="mt-6">
          <div className="relative flex items-center" data-testid="social-divider">
            <div className="flex-1 h-px bg-white/[0.06]"></div>
            <span className="px-3 text-[10px] uppercase tracking-wider text-[#71717A]">or continue with</span>
            <div className="flex-1 h-px bg-white/[0.06]"></div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 [&>*]:flex-1 [&>*]:min-w-[140px]">
            <GoogleSignInButton />
            <AppleSignInButton />
          </div>
        </div>

        <p className="text-center mt-6 text-[#A1A1AA]">
          Don't have an account?{' '}
          <Link to="/register" className="text-[#00F0FF] hover:underline" data-testid="register-link">
            Sign Up
          </Link>
        </p>

        {/* Shop owners have a fully-separate signup path — they MUST come in
            through here, not through the player /register flow. The two account
            types are distinct from creation. */}
        <Link
          to="/register-shop"
          className="mt-3 flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-gradient-to-r from-[#9D4CDD]/12 to-transparent border border-[#9D4CDD]/20 hover:border-[#9D4CDD]/40 transition-colors group"
          data-testid="login-shop-signup-cta"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#9D4CDD]/15 flex items-center justify-center">
              <span className="text-[#9D4CDD] text-lg">🏪</span>
            </div>
            <div className="text-left">
              <p className="text-white text-sm font-semibold">Run a game shop or cafe?</p>
              <p className="text-[#888] text-[10px]">Sign up as a Shop instead</p>
            </div>
          </div>
          <span className="text-[#9D4CDD] text-xs font-semibold group-hover:translate-x-0.5 transition-transform">→</span>
        </Link>

        <p className="text-center mt-4 text-[10px] text-[#555]">
          By continuing you agree to our{' '}
          <Link to="/terms" className="text-[#777] hover:text-[#9D4CDD] underline" data-testid="terms-link">Terms</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-[#777] hover:text-[#9D4CDD] underline" data-testid="privacy-link">Privacy Policy</Link>.
        </p>
      </motion.div>

      <FoundersWall />
    </div>
  );
}
